import { useNavigate, useParams } from 'react-router-dom'

import { PersonAvatarVariant } from '@factorialco/f0-react'
import { ResourceHeader } from '@factorialco/f0-react/dist/experimental'
import { Pencil } from '@factorialco/f0-react/icons/app'
import {
  ClassDetailHeaderInstructorsDocument,
  TrainingClassDetailIndexPageQuery,
} from 'generated/resources/graphql'
import moment from 'moment'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import buildUrl from 'modules/core/lib/buildUrl'
import { dateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

function getStringFromHtml(htmlString: string): string {
  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = htmlString

  // Replace <br> and <p> with newlines before extracting text
  tempDiv.querySelectorAll('br').forEach((br) => br.replaceWith('\n'))
  tempDiv.querySelectorAll('p').forEach((p) => {
    const newline = document.createTextNode('\n')
    p.appendChild(newline)
  })

  return tempDiv.textContent || tempDiv.innerText || ''
}

const ClassDetailHeader = ({
  trainingClassInfo
}: {
  trainingClassInfo: NonNullable<TrainingClassDetailIndexPageQuery['trainings']['trainingClass']>
}) => {
  const { id } = useParams()

  const navigate = useNavigate()
  const { navigation } = useNavigation()

  const { data: instructorsData } = useQuery(ClassDetailHeaderInstructorsDocument, {
    variables: { trainingClassId: trainingClassInfo.id },
  })

  const members = trainingClassInfo.classMembershipsConnection.nodes
  const avatarsList: PersonAvatarVariant[] = members.length
    ? members.map(({ access }) => ({
        firstName: access?.firstName ?? '',
        lastName: access?.lastName ?? '',
        type: 'person',
        src: buildUrl(access?.avatar ?? '')
      }))
    : []

  const description = trainingClassInfo?.description
    ? getStringFromHtml(trainingClassInfo?.description)
    : ''

  const getUniqueSessionsInstructors = (): PersonAvatarVariant[] => {
    const instructorNodes = instructorsData?.trainings.sessionInstructorsConnection.nodes ?? []
    const instructorsSet = new Set<string>()
    const uniqueInstructors = instructorNodes.filter(
      (item) => !instructorsSet.has(item.employee.id) && instructorsSet.add(item.employee.id)
    )
    return uniqueInstructors.map((instructor) => ({
      firstName: instructor.employee.firstName,
      lastName: instructor.employee.lastName,
      type: 'person' as const,
      src: buildUrl(instructor.employee?.avatar?.url ?? ''),
    }))
  }
  return (
    <ResourceHeader
      title={trainingClassInfo.name}
      description={description}
      metadata={[
        {
          label: i18n.t('trainings.trainings_revamp.classes.header.startDate'),
          value: {
            type: 'text',
            content: dateToString(moment(trainingClassInfo.startDate), 'ui_dmy_long')
          }
        },
        {
          label: i18n.t('trainings.trainings_revamp.classes.header.endDate'),
          value: {
            type: 'text',
            content: dateToString(moment(trainingClassInfo.endDate), 'ui_dmy_long')
          }
        },
        {
          label: i18n.t('trainings.trainings_revamp.classes.header.participants'),
          value: {
            type: 'list',
            variant: 'person',
            avatars: avatarsList
          }
        },
        {
          label: i18n.t('trainings.trainings_revamp.classes.header.instructors'),
          value: {
            type: 'list' as const,
            variant: 'person' as const,
            avatars: getUniqueSessionsInstructors()
          }
        },
        ...(trainingClassInfo.budgetMovement?.budget
          ? [
              {
                label: i18n.t('trainings.trainings_revamp.classes.header.training_budget'),
                value: {
                  type: 'text' as const,
                  content: trainingClassInfo.budgetMovement.budget.name
                }
              }
            ]
          : [])
      ]}
      secondaryActions={[
        {
          label: i18n.t('trainings.trainings_revamp.classes.header.delete'),
          onClick: () =>
            navigate(
              navigation.trainings.list.details_revamp.view.trainingClasses.detail.sessions.deleteClass.fullPath(
                {
                  id: String(id),
                  classId: String(trainingClassInfo.id),
                }
              )
            )
        },
        {
          label: i18n.t('trainings.trainings_revamp.classes.header.edit'),
          icon: Pencil,
          onClick: () =>
            navigate(
              navigation.trainings.list.details_revamp.view.trainingClasses.detail.sessions.editClass.fullPath(
                {
                  id: String(id),
                  classId: String(trainingClassInfo.id),
                }
              )
            )
        }
      ].filter(Boolean)}
    />
  )
}
export default ClassDetailHeader
